import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generatePDF } from '../utils/pdfGenerator';
import { letterTemplates } from '../utils/letterTemplates';

const { FiDownload, FiArrowLeft, FiEdit, FiEye } = FiIcons;

const PDFPreview = ({ formData, onBack, onEdit }) => {
  const previewRef = useRef();

  const handleDownloadPDF = async () => {
    try {
      await generatePDF(formData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const template = letterTemplates[formData.language];
  const formattedDate = new Date(formData.date).toLocaleDateString(
    formData.language === 'spanish' ? 'es-ES' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );

  const processTemplate = (text) => {
    return text
      .replace(/{{fecha}}/g, formattedDate)
      .replace(/{{recipient_name}}/g, formData.recipientName)
      .replace(/{{agent_name}}/g, formData.agentName)
      .replace(/{{office}}/g, formData.office)
      .replace(/{{city}}/g, formData.city)
      .replace(/{{agent_title}}/g, formData.agentTitle || 'Prosperity Leaders™ Representative')
      .replace(/{{additional_notes}}/g, formData.additionalNotes || '');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl shadow-xl overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <SafeIcon icon={FiEye} className="text-white text-2xl" />
            <div>
              <h2 className="text-2xl font-bold text-white">Letter Preview</h2>
              <p className="text-blue-100">Review before downloading</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="bg-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Back</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onEdit}
              className="bg-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiEdit} />
              <span>Edit</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDownloadPDF}
              className="bg-white text-blue-600 px-6 py-2 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} />
              <span>Download PDF</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Letter Preview */}
      <div className="p-8">
        <div 
          ref={previewRef}
          className="max-w-4xl mx-auto bg-white border border-gray-200 shadow-lg"
          style={{ 
            fontFamily: 'Roboto, Open Sans, sans-serif',
            fontSize: '11pt',
            lineHeight: '1.2',
            minHeight: '11in',
            width: '8.5in',
            margin: '0 auto',
            padding: '0.75in' // Updated to 0.75in margins
          }}
        >
          {/* Updated Header with Logo and Proper Alignment */}
          <div className="flex items-start mb-6" style={{ marginTop: '0.5in' }}>
            <img 
              src="https://media.publit.io/file/ProsperityLeaders/Prosperity-Elephant-WIDE-BLUEPNG.png"
              alt="Prosperity Leaders Logo"
              style={{ 
                height: '40px',
                width: 'auto',
                marginRight: '15px',
                marginTop: '2px'
              }}
            />
            <div>
              <h1 className="text-2xl font-bold mb-1" style={{ color: '#1B4C87', lineHeight: '1.2' }}>
                Prosperity Leaders™
              </h1>
              <p className="text-sm mb-1" style={{ color: '#00AAFF', lineHeight: '1.2' }}>
                Enterprise Corp.
              </p>
              <p className="text-sm" style={{ color: '#666666', lineHeight: '1.2' }}>
                Financial Education & Community Outreach
              </p>
            </div>
          </div>

          {/* Updated Blue Divider Line */}
          <div className="mb-8">
            <div 
              style={{ 
                borderTop: '3px solid #00AAFF',
                width: '90%',
                marginLeft: '0'
              }}
            ></div>
          </div>

          {/* Letter Content */}
          <div className="space-y-4">
            <div className="text-right mb-6">
              <p><strong>{template.dateLabel}:</strong> {formattedDate}</p>
            </div>

            <div className="mb-6">
              <p><strong>{template.greeting} {formData.recipientName}:</strong></p>
            </div>

            <div className="space-y-4 text-justify">
              {processTemplate(template.content).split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </div>

            {formData.additionalNotes && (
              <div className="my-6 p-4 bg-gray-50 rounded-lg">
                <p>{formData.additionalNotes}</p>
              </div>
            )}

            <div className="mt-8">
              <p className="mb-6">{template.closing}</p>
              
              {/* Updated Signature Order - Representative First */}
              <div className="space-y-6">
                <div className="mt-8">
                  <div className="border-b border-gray-400 w-80 mb-2"></div>
                  <p>{formData.agentName}, {formData.agentTitle || 'Prosperity Leaders™ Representative'}</p>
                  <p className="text-sm">Prosperity Leaders™ Representative</p>
                </div>
                
                <div className="mt-8">
                  <p className="font-bold">Jenny & Sebastian Rodriguez</p>
                  <p className="italic text-sm">
                    {formData.language === 'spanish' 
                      ? 'Fundadores; Prosperity Leaders Enterprise Corp.'
                      : 'Founders; Prosperity Leaders Enterprise Corp.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Updated Institution Information - Plain text version */}
            <div className="mt-8 pt-6 border-t" style={{ borderColor: '#D9D9D9' }}>
              <div className="text-sm" style={{ lineHeight: '1.2', whiteSpace: 'pre-wrap' }}>
                {template.recognition}
              </div>
            </div>
          </div>

          {/* Updated Footer with Clickable Links Preview */}
          <div className="mt-8 pt-4 border-t text-center text-sm" style={{ borderColor: '#D9D9D9', marginBottom: '0.5in' }}>
            <div className="space-y-1">
              <p className="font-semibold">Prosperity Leaders Enterprise Corp.</p>
              <p>632 Newark Avenue, Suite 212, Jersey City, NJ 07306</p>
              <p>P: 551-325-3211</p>
              <p>
                <span style={{ color: '#00AAFF', textDecoration: 'underline', cursor: 'pointer' }}>
                  ProsperityLeaders.net
                </span>
                {' | '}
                <span style={{ color: '#00AAFF', textDecoration: 'underline', cursor: 'pointer' }}>
                  Bloomberg Businessweek
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PDFPreview;